/**
 * HowToGuide.js — interactive "How to use this page" panel shown at the bottom of every screen.
 *
 *  • The guide is picked by the screen (tab) and, on a role's main screen, by the role —
 *    so each person only sees instructions for their own work.
 *  • Each task opens into numbered steps with tips, “What happens next” and “Avoid these mistakes”.
 *  • 👆 Show me      — finds the real button / field on the page and makes it glow.
 *  • ▶ Guide me      — a floating coach that walks through the task one step at a time.
 *  • ✓ ticks         — staff tick steps as they go (progress bars, celebration when done).
 *  • 🔍 search       — “What do you want to do?” filters tasks across steps and tips.
 *  • 📘 Help button  — floats on screen and jumps to the guide from anywhere on the page.
 *
 * Content lives in ./howto/guides.*.js — edit the wording there and redeploy (./deploy.sh).
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { GUIDES as G, BASICS, COMMON_FAQ } from './howto';

/* ═══════════════════════════════════════════════════════════════════════════
   Routing: which guide belongs to which screen
   ═══════════════════════════════════════════════════════════════════════════ */

// Admin Panel internal tabs → guide ids (department views reuse the department guides)
export const ADMIN_TAB_GUIDES = {
  overview: 'admin_overview', reporting: 'admin_reporting', reports: 'admin_reports',
  users: 'admin_users', employee_profiles: 'admin_employee_profiles', guest_history: 'admin_guest_history',
  bulk_import: 'admin_bulk_import', rooms: 'admin_rooms', food: 'admin_menu', bar: 'admin_menu',
  gate_log: 'admin_gate_log', branding: 'admin_branding', backup: 'admin_backup', reviews: 'admin_reviews',
  landing: 'admin_landing', seo: 'seo', complimentary: 'admin_complimentary', donations: 'admin_donations',
  currencies: 'admin_currencies', leave: 'admin_leave', session: 'admin_session', devtools: 'admin_devtools',
  accounting: 'main_accountant', payroll: 'payroll', audit_trail: 'audit', marketing: 'main_marketing',
  reservations: 'reservations', conference_bookings: 'admin_conference_bookings', transport: 'admin_transport',
  attendance: 'admin_attendance', folios: 'folio', housekeeping: 'main_housekeeping', pool_kids: 'swimming',
  food_costing: 'main_chef', gift_shop: 'main_gift_shop', storeroom: 'main_storekeeper',
  reception: 'main_reception', cashier: 'main_cashier', kitchen: 'main_kitchen',
  kitchen_prep: 'admin_kitchen_prep', waiter: 'main_waiter',
};

// Sidebar screens that share a guide with a role's main screen
const ALIASES = {
  gate: 'main_gate', housekeeping: 'main_housekeeping', accounting: 'main_accountant',
  store: 'main_storekeeper', giftshop: 'main_gift_shop', marketing: 'main_marketing',
  costing: 'main_chef', main_general_manager: 'gm_dashboard',
};

export function guideKey(tab, role) {
  const key = tab === 'main' ? `main_${role}` : tab;
  if (G[key]) return key;
  return ALIASES[key] && G[ALIASES[key]] ? ALIASES[key] : null;
}

/** All guide ids — used to verify coverage. */
export const GUIDE_IDS = Object.keys(G);

/* ═══════════════════════════════════════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════════════════════════════════════ */

const normStep = (s) => (typeof s === 'string' ? { t: s } : s);
const plain = (s) => (s || '').replace(/\*\*/g, '');
const chipsOf = (s) => Array.from((s || '').matchAll(/\*\*(.+?)\*\*/g)).map(m => m[1]);

/** Render text with **chips** as highlighted button/field names. */
function Rich({ text }) {
  const parts = (text || '').split(/(\*\*.+?\*\*)/g);
  return parts.map((p, i) => (p.startsWith('**') && p.endsWith('**') && p.length > 4
    ? <b key={i} className="howto-chip">{p.slice(2, -2)}</b>
    : <React.Fragment key={i}>{p}</React.Fragment>));
}

/* Pick an icon for a task from its heading */
function taskIcon(h) {
  const m = h.match(/^(\p{Extended_Pictographic}️?)/u);
  if (m) return m[1];
  const t = h.toLowerCase();
  const map = [
    ['folio', '📒'], ['charge', '🧾'], ['check in', '🛎️'], ['check-in', '🛎️'], ['check out', '🚪'], ['checkout', '🚪'], ['conference', '🏛️'],
    ['transport', '🚐'], ['trip', '🚗'], ['invoice', '🧾'], ['reprint', '🖨️'], ['print', '🖨️'],
    ['order', '🍽️'], ['kitchen', '👨‍🍳'], ['station', '📍'], ['prepare', '🔥'], ['serve', '🛎️'],
    ['payment', '💳'], ['pay ', '💳'], ['pay', '💳'], ['bill', '💳'], ['refund', '↩️'], ['void', '⛔'], ['discount', '🏷️'],
    ['complimentary', '🎁'], ['pool', '🏊'], ['swim', '🩱'], ['kids', '🧒'], ['child', '🧒'], ['donation', '🌿'],
    ['menu', '📋'], ['price', '🏷️'], ['return', '↩️'], ['shopping', '🛒'], ['order stock', '🛒'], ['receive', '📦'],
    ['stock', '📦'], ['issue', '📤'], ['ingredient', '🥕'], ['recipe', '📖'], ['scale', '⚖️'], ['spoilage', '🗑️'],
    ['budget', '💰'], ['clean', '🧹'], ['inspect', '🔍'], ['broken', '⚠️'], ['repair', '🔧'], ['routine', '🗓️'],
    ['maintenance', '🔧'], ['cost', '💰'], ['expense', '🧾'], ['vat', '🏛️'], ['tax', '🏛️'], ['asset', '🏢'],
    ['fund', '💸'], ['journal', '📒'], ['ledger', '📒'], ['statement', '📊'], ['chart of accounts', '🗂️'],
    ['supplier', '🏭'], ['overdue', '⏱️'], ['payroll', '💼'], ['payslip', '📄'], ['employee', '🪪'], ['leave', '📅'],
    ['attendance', '🕐'], ['clock', '🕐'], ['password', '🔑'], ['profile', '👤'], ['complaint', '⚠️'], ['nps', '⭐'],
    ['satisfaction', '⭐'], ['campaign', '📣'], ['promotion', '🏷️'], ['sms', '📱'], ['email', '✉️'], ['ai', '🤖'],
    ['keyword', '🔍'], ['competitor', '🏆'], ['titles', '🏷️'], ['investigate', '🕵️'], ['risk', '🚨'],
    ['visitor', '👥'], ['inside', '👥'], ['export', '📤'], ['search', '🔎'], ['find', '🔎'], ['look up', '🔎'],
    ['brand', '🎨'], ['identity', '🎨'], ['logo', '🖼️'], ['stamp', '🔏'], ['colour', '🎨'], ['photo', '🖼️'],
    ['website', '🌐'], ['layout', '🎨'], ['backup', '💾'], ['restore', '♻️'], ['login rule', '🔐'], ['import', '📥'],
    ['account', '👥'], ['suspend', '⏸️'], ['set up', '🚀'], ['routine', '🗓️'], ['snapshot', '📸'], ['attention', '🚨'],
    ['tile', '📊'], ['month', '📅'], ['chart', '📈'], ['report', '📑'], ['performance', '📈'], ['zone', '📍'],
    ['exchange', '💱'], ['archive', '🗂️'], ['room', '🏨'], ['basics', '⭐'], ['close the day', '🌙'], ['developer', '🛠️'],
  ];
  for (const [k, v] of map) if (t.includes(k)) return v;
  return '📌';
}

/* ═══════════════════════════════════════════════════════════════════════════
   “Show me” — find the real button / tab / field on the page that a step mentions
   ═══════════════════════════════════════════════════════════════════════════ */
const STOP = new Set(['cancel', 'close', 'edit', 'view', 'delete', 'del', 'all', 'save', 'clear', 'today', 'total', 'status', 'yes', 'no',
  'back', 'next', 'open', 'print', 'pending', 'active', 'search', 'date', 'from', 'to', 'type', 'name', 'phone', 'email', 'notes',
  'amount', 'category', 'reason', 'description']);
const norm = (s) => (s || '')
  .replace(/\p{Extended_Pictographic}|️|‍/gu, ' ')
  .replace(/\(\s*\d+\s*\)/g, ' ')
  .replace(/[＋+✓✕✗▶↩⬇⬆↻←→↓▲▼•·*…]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()
  .toLowerCase();

function findTargetFor(stepText) {
  if (typeof document === 'undefined') return null;
  const chips = chipsOf(stepText).map(norm).filter(c => c.length >= 3 && !STOP.has(c));
  const want = norm(plain(stepText));
  if (!want) return null;
  const guideRoots = Array.from(document.querySelectorAll('[data-howto-root]'));
  const inGuide = (el) => guideRoots.some(r => r.contains(el));
  const visible = (el) => {
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && getComputedStyle(el).visibility !== 'hidden';
  };
  let best = null; let bestScore = 0;
  const consider = (el, label, bonus, highlightEl) => {
    if (!label || label.length < 3 || STOP.has(label)) return;
    let score = 0;
    // 1) exact match with a **chip** in the step — strongest signal
    if (chips.some(c => c === label)) score = 100 + label.length;
    else if (chips.some(c => c.length >= 4 && (label.startsWith(c) || c.startsWith(label)) && Math.min(c.length, label.length) >= 4)) score = 60 + Math.min(label.length, 30);
    // 2) the label appears as whole words inside the step text
    else if (label.length >= 4 && want.includes(label)) {
      const re = new RegExp(`(^|[^a-z0-9])${label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^a-z0-9]|$)`);
      if (re.test(want)) score = label.length;
    }
    if (!score) return;
    score += bonus;
    if (score > bestScore) { bestScore = score; best = highlightEl || el; }
  };
  document.querySelectorAll('button, [role="button"], [role="tab"], a[href], select').forEach(el => {
    if (inGuide(el) || !visible(el)) return;
    consider(el, norm(el.innerText || el.value || el.getAttribute('aria-label') || el.title), 10);
  });
  // Field labels: short leaf text whose container holds an input
  document.querySelectorAll('label, div, span').forEach(el => {
    if (el.childElementCount > 0 || inGuide(el)) return;
    const txt = norm(el.textContent);
    if (txt.length < 3 || txt.length > 40) return;
    const box = el.parentElement;
    if (!box || !box.querySelector('input, select, textarea') || !visible(box)) return;
    consider(el, txt, 0, box);
  });
  return best;
}

let hlTimer = null;
function highlight(el) {
  if (!el) return;
  document.querySelectorAll('.howto-hl').forEach(n => n.classList.remove('howto-hl'));
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  el.classList.add('howto-hl');
  clearTimeout(hlTimer);
  hlTimer = setTimeout(() => el.classList.remove('howto-hl'), 4500);
}

/* ═══════════════════════════════════════════════════════════════════════════
   Styles & animations (injected once)
   ═══════════════════════════════════════════════════════════════════════════ */
const CSS = `
@keyframes howtoFadeUp { from { opacity: 0; transform: translateY(12px) } to { opacity: 1; transform: none } }
@keyframes howtoPop { 0% { transform: scale(.5) } 60% { transform: scale(1.2) } 100% { transform: scale(1) } }
@keyframes howtoPulse { 0%,100% { box-shadow: 0 0 0 3px rgba(201,168,76,.95), 0 0 0 0 rgba(201,168,76,.55) } 50% { box-shadow: 0 0 0 3px rgba(201,168,76,.95), 0 0 0 14px rgba(201,168,76,0) } }
@keyframes howtoSlideIn { from { opacity: 0; transform: translateY(26px) scale(.97) } to { opacity: 1; transform: none } }
@keyframes howtoStepIn { from { opacity: 0; transform: translateX(26px) } to { opacity: 1; transform: none } }
@keyframes howtoShine { from { background-position: -200% 0 } to { background-position: 200% 0 } }
@keyframes howtoBounce { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-3px) } }
@keyframes howtoFloat { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-4px) } }
@keyframes howtoRing { 0% { box-shadow: 0 0 0 0 rgba(201,168,76,.6) } 100% { box-shadow: 0 0 0 16px rgba(201,168,76,0) } }
@keyframes howtoConfetti { 0% { opacity: 1; transform: translate(0,0) rotate(0) } 100% { opacity: 0; transform: translate(var(--dx), var(--dy)) rotate(var(--rot)) } }
@keyframes howtoWave { 0%,100% { transform: rotate(0) } 25% { transform: rotate(-12deg) } 75% { transform: rotate(12deg) } }
.howto-hl { position: relative; z-index: 5; border-radius: 8px; animation: howtoPulse 1.1s ease-in-out 4; outline: none !important; }
.howto-fade { animation: howtoFadeUp .4s cubic-bezier(.2,.8,.3,1) both }
.howto-stepin { animation: howtoStepIn .35s cubic-bezier(.2,.8,.3,1) both }
.howto-task { transition: border-color .2s, box-shadow .25s, transform .2s }
.howto-task:hover { border-color: var(--primary) !important; box-shadow: 0 8px 24px rgba(0,0,0,.08); transform: translateY(-1px) }
.howto-collapse { display: grid; grid-template-rows: 0fr; transition: grid-template-rows .38s cubic-bezier(.2,.8,.3,1) }
.howto-collapse.open { grid-template-rows: 1fr }
.howto-collapse > div { overflow: hidden }
.howto-step-num { transition: background .2s, color .2s, transform .2s }
.howto-step-num:hover { transform: scale(1.12) }
.howto-done { animation: howtoPop .4s ease }
.howto-btn { transition: transform .15s, filter .15s, background .2s, box-shadow .2s }
.howto-btn:hover { transform: translateY(-1px); filter: brightness(1.06) }
.howto-btn:active { transform: translateY(0) scale(.97) }
.howto-showme { animation: howtoBounce 1.2s ease-in-out 3 }
.howto-coach { animation: howtoSlideIn .4s cubic-bezier(.2,.9,.3,1.2) both }
.howto-bar > i { display: block; height: 100%; border-radius: 99px; background: linear-gradient(90deg, var(--primary), #e8cf86, var(--primary)); background-size: 200% 100%; animation: howtoShine 2.5s linear infinite; transition: width .45s cubic-bezier(.2,.8,.3,1) }
.howto-chip { display: inline-block; padding: 0 6px; margin: 0 1px; border-radius: 6px; font-weight: 700; font-size: .95em; color: var(--textPrimary); background: var(--bgHover); border: 1px solid var(--border); line-height: 1.5; white-space: normal }
.howto-fab { animation: howtoSlideIn .35s ease both, howtoFloat 3s ease-in-out .4s infinite }
.howto-fab::after { content: ''; position: absolute; inset: 0; border-radius: inherit; animation: howtoRing 2s ease-out 1s 3 }
.howto-wave { display: inline-block; animation: howtoWave 1.2s ease-in-out 2 }
.howto-confetti { position: absolute; top: 40%; left: 50%; font-size: 18px; pointer-events: none; animation: howtoConfetti 1.1s cubic-bezier(.2,.8,.3,1) forwards }
.howto-search:focus { border-color: var(--primary) !important; box-shadow: 0 0 0 3px rgba(201,168,76,.18) }
.howto-tabbtn { transition: background .2s, color .2s }
@media (max-width: 560px) { .howto-taskbody { padding-left: 16px !important } .howto-fab { padding: 10px 13px !important; font-size: 12.5px !important } }
@media (prefers-reduced-motion: reduce) {
  .howto-hl, .howto-fade, .howto-stepin, .howto-coach, .howto-done, .howto-showme, .howto-bar > i, .howto-fab, .howto-fab::after, .howto-wave, .howto-confetti { animation: none !important }
  .howto-collapse { transition: none }
}
`;
function useHowtoCss() {
  useEffect(() => {
    if (document.getElementById('howto-css-v2')) return;
    const st = document.createElement('style');
    st.id = 'howto-css-v2';
    st.textContent = CSS;
    document.head.appendChild(st);
  }, []);
}

/* Re-scan the page for “Show me” targets while a task is open */
function useTargets(steps, active) {
  const [targets, setTargets] = useState([]);
  useEffect(() => {
    if (!active) return undefined;
    const scan = () => setTargets(steps.map(s => !!findTargetFor(s.t)));
    scan();
    const iv = setInterval(scan, 1500);
    return () => clearInterval(iv);
  }, [active, steps]);
  return targets;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Small pieces
   ═══════════════════════════════════════════════════════════════════════════ */

/** Small button that scrolls down to this page's guide. */
export function HowToJumpButton({ style = {} }) {
  const go = () => {
    const el = document.getElementById('howto-guide');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <button onClick={go} title="Jump to the how-to guide for this page" className="howto-btn"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 6,
        border: '1px solid var(--border)', background: 'var(--bgCard)', color: 'var(--textSecond)',
        fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', ...style }}>
      📘 How to use this page ↓
    </button>
  );
}

function Callout({ kind, title, children }) {
  const k = {
    tip:    { icon: '💡', color: 'var(--primary)', bg: 'var(--bgHover)' },
    result: { icon: '✅', color: '#22c55e', bg: '#22c55e12' },
    avoid:  { icon: '⚠️', color: '#ef4444', bg: '#ef44440f' },
  }[kind];
  return (
    <div className="howto-fade" style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, lineHeight: 1.6,
      padding: '10px 12px', borderRadius: 10, borderLeft: `4px solid ${k.color}`, background: k.bg, color: 'var(--textSecond)' }}>
      <span style={{ fontSize: 16, lineHeight: 1.4 }}>{k.icon}</span>
      <div style={{ minWidth: 0 }}>
        {title && <div style={{ fontWeight: 800, color: 'var(--textPrimary)', fontSize: 12, textTransform: 'uppercase', letterSpacing: .6, marginBottom: 2 }}>{title}</div>}
        {children}
      </div>
    </div>
  );
}

function Confetti() {
  const bits = useMemo(() => Array.from({ length: 16 }, (_, i) => ({
    e: ['🎉', '✨', '⭐', '🎊'][i % 4],
    dx: `${Math.round(Math.cos((i / 16) * Math.PI * 2) * (70 + Math.random() * 50))}px`,
    dy: `${Math.round(Math.sin((i / 16) * Math.PI * 2) * (55 + Math.random() * 40) - 20)}px`,
    rot: `${Math.round(Math.random() * 360)}deg`,
    delay: `${Math.round(Math.random() * 120)}ms`,
  })), []);
  return bits.map((b, i) => (
    <span key={i} className="howto-confetti" style={{ '--dx': b.dx, '--dy': b.dy, '--rot': b.rot, animationDelay: b.delay }}>{b.e}</span>
  ));
}

/* ═══════════════════════════════════════════════════════════════════════════
   One task card
   ═══════════════════════════════════════════════════════════════════════════ */
function TaskCard({ task, idx, open, onToggle, done, onToggleStep, onResetTask, onCoach, delay, query }) {
  const targets = useTargets(task.steps, open);
  const total = task.steps.length;
  const doneCount = done.size;
  const complete = doneCount === total;
  const [justDone, setJustDone] = useState(false);
  const prev = useRef(complete);
  useEffect(() => {
    if (complete && !prev.current) { setJustDone(true); const t = setTimeout(() => setJustDone(false), 1400); prev.current = complete; return () => clearTimeout(t); }
    prev.current = complete;
    return undefined;
  }, [complete]);

  return (
    <div className="howto-task howto-fade" style={{ animationDelay: `${delay}ms`, position: 'relative',
      border: `1px solid ${complete ? '#22c55e66' : open ? 'var(--primary)' : 'var(--border)'}`,
      borderRadius: 14, background: 'var(--bgSurface, var(--bgCard))', overflow: 'hidden' }}>
      {justDone && <Confetti />}
      <button onClick={onToggle} aria-expanded={open}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '13px 14px', background: 'transparent',
          border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', color: 'var(--textPrimary)' }}>
        <span className={complete ? 'howto-done' : ''} style={{ width: 40, height: 40, flexShrink: 0, borderRadius: 11, display: 'grid', placeItems: 'center', fontSize: 20,
          background: complete ? '#22c55e1f' : 'var(--bgHover)' }}>{complete ? '✅' : task.icon}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 14.5, fontWeight: 750 }}>{task.h}</span>
          {task.d && <span style={{ display: 'block', fontSize: 12.5, color: 'var(--textMuted)', marginTop: 2 }}>{task.d}</span>}
        </span>
        <span style={{ fontSize: 11, fontWeight: 800, padding: '3px 8px', borderRadius: 99, whiteSpace: 'nowrap',
          background: complete ? '#22c55e1f' : 'var(--bgHover)', color: complete ? '#22c55e' : 'var(--textMuted)' }}>
          {doneCount}/{total} steps
        </span>
        <span style={{ fontSize: 12, color: 'var(--textMuted)', transition: 'transform .3s', transform: open ? 'rotate(180deg)' : 'none' }}>▼</span>
      </button>

      <div className={`howto-collapse${open ? ' open' : ''}`}>
        <div>
          <div className="howto-taskbody" style={{ padding: '2px 16px 16px 66px' }}>
            <div className="howto-bar" style={{ height: 6, background: 'var(--border)', borderRadius: 99, marginBottom: 14, overflow: 'hidden' }}>
              <i style={{ width: `${(doneCount / total) * 100}%` }} />
            </div>

            <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 12 }}>
              {task.steps.map((st, i) => {
                const isDone = done.has(i);
                const hit = query && (plain(st.t) + ' ' + (st.tip || '')).toLowerCase().includes(query);
                return (
                  <li key={i} style={{ display: 'flex', gap: 11, alignItems: 'flex-start', position: 'relative',
                    background: hit ? 'var(--bgHover)' : 'transparent', borderRadius: 8, padding: hit ? '4px 6px' : 0 }}>
                    {/* connector line between step bubbles */}
                    {i < total - 1 && <span aria-hidden style={{ position: 'absolute', left: 13, top: 30, bottom: -12, width: 2,
                      background: isDone ? '#22c55e55' : 'var(--border)' }} />}
                    <button onClick={() => onToggleStep(i)} title={isDone ? 'Mark as not done' : 'Tick when you have done this step'}
                      className={`howto-step-num${isDone ? ' howto-done' : ''}`}
                      style={{ position: 'relative', zIndex: 1, width: 28, height: 28, flexShrink: 0, borderRadius: '50%', cursor: 'pointer', fontFamily: 'inherit',
                        fontSize: 12, fontWeight: 800, border: `2px solid ${isDone ? '#22c55e' : 'var(--primary)'}`,
                        background: isDone ? '#22c55e' : 'var(--bgCard)', color: isDone ? '#fff' : 'var(--primary)' }}>
                      {isDone ? '✓' : i + 1}
                    </button>
                    <div style={{ flex: 1, minWidth: 0, paddingTop: 3 }}>
                      <span style={{ fontSize: 13.5, lineHeight: 1.7, color: isDone ? 'var(--textFaint)' : 'var(--textSecond)',
                        textDecoration: isDone ? 'line-through' : 'none' }}><Rich text={st.t} /></span>
                      {targets[i] && !isDone && (
                        <button className="howto-btn howto-showme" onClick={() => highlight(findTargetFor(st.t))}
                          style={{ marginLeft: 8, padding: '2px 10px', fontSize: 11, fontWeight: 800, borderRadius: 99, cursor: 'pointer',
                            border: '1px solid var(--primary)', background: 'transparent', color: 'var(--primary)', fontFamily: 'inherit', verticalAlign: 'middle' }}>
                          👆 Show me
                        </button>
                      )}
                      {st.tip && !isDone && (
                        <div style={{ marginTop: 5, fontSize: 12.5, lineHeight: 1.55, color: 'var(--textMuted)', display: 'flex', gap: 6 }}>
                          <span>💡</span><span><Rich text={st.tip} /></span>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>

            {task.result && <Callout kind="result" title="What happens next"><Rich text={task.result} /></Callout>}
            {task.mistakes && task.mistakes.length > 0 && (
              <Callout kind="avoid" title="Avoid these mistakes">
                <ul style={{ margin: 0, paddingLeft: 16 }}>{task.mistakes.map((m, i) => <li key={i}><Rich text={m} /></li>)}</ul>
              </Callout>
            )}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
              <button className="howto-btn" onClick={() => onCoach(idx)}
                style={{ padding: '9px 16px', borderRadius: 9, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 800,
                  background: 'var(--primary)', color: '#fff', boxShadow: '0 4px 14px rgba(201,168,76,.35)' }}>
                ▶ Guide me step by step
              </button>
              {doneCount > 0 && (
                <button className="howto-btn" onClick={onResetTask}
                  style={{ padding: '9px 12px', borderRadius: 9, border: '1px solid var(--border)', cursor: 'pointer', fontFamily: 'inherit',
                    fontSize: 12, background: 'transparent', color: 'var(--textMuted)' }}>
                  ↺ Start again
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Floating step-by-step coach
   ═══════════════════════════════════════════════════════════════════════════ */
function Coach({ task, onClose, onStepDone }) {
  const [i, setI] = useState(0);
  const [finished, setFinished] = useState(false);
  const total = task.steps.length;
  const step = task.steps[i];
  const [hasTarget, setHasTarget] = useState(false);

  useEffect(() => {
    if (finished) return undefined;
    const t = findTargetFor(step.t);
    setHasTarget(!!t);
    if (t) highlight(t);
    const iv = setInterval(() => setHasTarget(!!findTargetFor(step.t)), 1500);
    return () => clearInterval(iv);
  }, [step, finished]);

  const next = useCallback(() => {
    onStepDone(i);
    if (i < total - 1) setI(i + 1); else setFinished(true);
  }, [i, total, onStepDone]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (finished) return;
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') setI(v => Math.max(0, v - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, finished, onClose]);

  return (
    <div data-howto-root className="howto-coach" role="dialog" aria-label={`Guide: ${task.h}`}
      style={{ position: 'fixed', right: 16, bottom: 16, zIndex: 10000, width: 'min(400px, calc(100vw - 32px))',
        background: 'var(--bgCard)', color: 'var(--textPrimary)', border: '1px solid var(--border)', borderRadius: 18,
        boxShadow: '0 22px 60px rgba(0,0,0,.28)', overflow: 'hidden' }}>
      <div style={{ padding: '13px 14px', display: 'flex', alignItems: 'center', gap: 10,
        background: 'linear-gradient(135deg, var(--bgSidebar, #1B3A2D), var(--primary))', color: '#fff' }}>
        <span style={{ fontSize: 22 }}>{task.icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10.5, opacity: .85, letterSpacing: .7, textTransform: 'uppercase', fontWeight: 700 }}>Guided task</div>
          <div style={{ fontSize: 14.5, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{task.h}</div>
        </div>
        <button onClick={onClose} aria-label="Close guide" className="howto-btn"
          style={{ background: 'rgba(255,255,255,.18)', border: 'none', color: '#fff', width: 30, height: 30, borderRadius: 9, cursor: 'pointer', fontSize: 14 }}>✕</button>
      </div>

      {!finished ? (
        <div style={{ padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--textMuted)', fontWeight: 800, marginBottom: 6 }}>
            <span>STEP {i + 1} OF {total}</span><span>{Math.round(((i + 1) / total) * 100)}%</span>
          </div>
          <div className="howto-bar" style={{ height: 7, background: 'var(--border)', borderRadius: 99, overflow: 'hidden', marginBottom: 10 }}>
            <i style={{ width: `${((i + 1) / total) * 100}%` }} />
          </div>
          {/* step dots */}
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 14 }}>
            {task.steps.map((_, n) => (
              <button key={n} onClick={() => setI(n)} aria-label={`Go to step ${n + 1}`} className="howto-btn"
                style={{ width: n === i ? 22 : 9, height: 9, borderRadius: 99, border: 'none', cursor: 'pointer', padding: 0,
                  background: n < i ? '#22c55e' : n === i ? 'var(--primary)' : 'var(--border)', transition: 'width .3s, background .3s' }} />
            ))}
          </div>

          <div key={i} className="howto-stepin" style={{ display: 'flex', gap: 12 }}>
            <span className="howto-done" style={{ width: 36, height: 36, flexShrink: 0, borderRadius: '50%', display: 'grid', placeItems: 'center',
              background: 'var(--primary)', color: '#fff', fontWeight: 800, fontSize: 15 }}>{i + 1}</span>
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--textPrimary)' }}><Rich text={step.t} /></p>
              {step.tip && (
                <div style={{ marginTop: 8, fontSize: 12.5, lineHeight: 1.55, color: 'var(--textSecond)', background: 'var(--bgHover)',
                  borderRadius: 9, padding: '8px 10px', display: 'flex', gap: 6 }}>
                  <span>💡</span><span><Rich text={step.tip} /></span>
                </div>
              )}
            </div>
          </div>

          <div style={{ marginTop: 12, fontSize: 12, color: hasTarget ? 'var(--primary)' : 'var(--textFaint)' }}>
            {hasTarget
              ? <button className="howto-btn howto-showme" onClick={() => highlight(findTargetFor(step.t))}
                  style={{ border: '1px dashed var(--primary)', background: 'transparent', color: 'var(--primary)', borderRadius: 9,
                    padding: '6px 11px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 800 }}>👆 Show me where</button>
              : <span>👀 Open the tab or form this step mentions — the button glows when it is on screen.</span>}
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button className="howto-btn" onClick={() => setI(Math.max(0, i - 1))} disabled={i === 0}
              style={{ flex: 1, padding: 10, borderRadius: 10, border: '1px solid var(--border)', background: 'transparent',
                color: 'var(--textSecond)', cursor: i === 0 ? 'default' : 'pointer', opacity: i === 0 ? .45 : 1, fontFamily: 'inherit', fontWeight: 700 }}>← Back</button>
            <button className="howto-btn" onClick={next}
              style={{ flex: 2, padding: 10, borderRadius: 10, border: 'none', background: 'var(--primary)', color: '#fff',
                cursor: 'pointer', fontFamily: 'inherit', fontWeight: 800 }}>
              {i < total - 1 ? 'Done — next step →' : 'Finish ✓'}
            </button>
          </div>
          <div style={{ marginTop: 10, fontSize: 10.5, color: 'var(--textFaint)', textAlign: 'center' }}>← → keys move between steps · Esc closes</div>
        </div>
      ) : (
        <div className="howto-fade" style={{ padding: '26px 18px 18px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
          <Confetti />
          <div className="howto-done" style={{ fontSize: 48 }}>🎉</div>
          <div style={{ fontSize: 17, fontWeight: 800, marginTop: 6 }}>Well done — task complete!</div>
          <div style={{ fontSize: 13, color: 'var(--textMuted)', marginTop: 4 }}>You finished “{task.h}”.</div>
          {task.result && (
            <div style={{ textAlign: 'left' }}><Callout kind="result" title="What happens next"><Rich text={task.result} /></Callout></div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button className="howto-btn" onClick={() => { setI(0); setFinished(false); }}
              style={{ flex: 1, padding: 10, borderRadius: 10, border: '1px solid var(--border)', background: 'transparent', color: 'var(--textSecond)', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700 }}>↺ Again</button>
            <button className="howto-btn" onClick={onClose}
              style={{ flex: 1, padding: 10, borderRadius: 10, border: 'none', background: 'var(--primary)', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 800 }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Floating Help button — visible while the guide is off-screen
   ═══════════════════════════════════════════════════════════════════════════ */
function HelpFab({ targetRef, hidden, onClick, title }) {
  const [offscreen, setOffscreen] = useState(false);
  useEffect(() => {
    const el = targetRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(([e]) => setOffscreen(!e.isIntersecting), { threshold: 0.05 });
    io.observe(el);
    return () => io.disconnect();
  }, [targetRef]);
  if (!offscreen || hidden) return null;
  return createPortal(
    <button data-howto-root onClick={onClick} className="howto-btn howto-fab" title={`How to use ${title}`}
      style={{ position: 'fixed', right: 18, bottom: 18, zIndex: 9999, display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '11px 16px', borderRadius: 99, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 800,
        color: '#fff', background: 'linear-gradient(135deg, var(--bgSidebar, #1B3A2D), var(--primary))', boxShadow: '0 10px 28px rgba(0,0,0,.25)' }}>
      <span style={{ fontSize: 17 }}>📘</span> Help: how to use this page
    </button>,
    document.body
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Main panel
   ═══════════════════════════════════════════════════════════════════════════ */
export default function HowToGuide({ tab, role, guideId, basics = false }) {
  useHowtoCss();
  const key = guideId ? (G[guideId] ? guideId : null) : guideKey(tab, role);
  const storeKey = `howto_open_${key}`;
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(storeKey) !== '0'; } catch { return true; }
  });
  const [query, setQuery] = useState('');
  const [openTask, setOpenTask] = useState(0);
  const [doneMap, setDoneMap] = useState({});
  const [coach, setCoach] = useState(null);
  const [faqOpen, setFaqOpen] = useState(null);
  const rootRef = useRef(null);

  const tasks = useMemo(() => {
    if (!key) return [];
    const g = G[key];
    const secs = basics ? [...g.sections, BASICS] : g.sections;
    return secs.map(s => ({ ...s, icon: taskIcon(s.h), steps: s.steps.map(normStep) }));
  }, [key, basics]);

  if (!key) return null;
  const g = G[key];
  const faq = [...(g.faq || []), ...COMMON_FAQ];

  const q = query.trim().toLowerCase();
  const matches = (t) => !q || t.h.toLowerCase().includes(q) || (t.d || '').toLowerCase().includes(q)
    || t.steps.some(s => (plain(s.t) + ' ' + (s.tip || '')).toLowerCase().includes(q))
    || (t.result || '').toLowerCase().includes(q);
  const shown = tasks.map((t, i) => ({ t, i })).filter(({ t }) => matches(t));

  const totalSteps = tasks.reduce((n, t) => n + t.steps.length, 0);
  const doneSteps = Object.values(doneMap).reduce((n, s) => n + s.size, 0);
  const doneTasks = tasks.filter((t, i) => (doneMap[i] || new Set()).size === t.steps.length).length;

  const setOpenPersist = (v) => {
    setOpen(v);
    try { localStorage.setItem(storeKey, v ? '1' : '0'); } catch {}
  };
  const toggleStep = (ti, si, force) => setDoneMap(m => {
    const cur = new Set(m[ti] || []);
    if (force === true) cur.add(si); else if (cur.has(si)) cur.delete(si); else cur.add(si);
    return { ...m, [ti]: cur };
  });
  const resetTask = (ti) => setDoneMap(m => ({ ...m, [ti]: new Set() }));
  const jumpHere = () => {
    setOpenPersist(true);
    rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div ref={rootRef} id="howto-guide" data-howto-root className="howto-fade"
      style={{ marginTop: 28, border: '1px solid var(--border)', borderRadius: 18, background: 'var(--bgCard)', overflow: 'hidden',
        scrollMarginTop: 80, boxShadow: '0 6px 28px rgba(0,0,0,.06)' }}>
      {/* Header */}
      <button onClick={() => setOpenPersist(!open)} aria-expanded={open}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 14, padding: '18px 20px', border: 'none', cursor: 'pointer',
          textAlign: 'left', fontFamily: 'inherit', color: '#fff',
          background: 'linear-gradient(135deg, var(--bgSidebar, #1B3A2D) 0%, var(--primary) 140%)' }}>
        <span style={{ width: 48, height: 48, borderRadius: 14, display: 'grid', placeItems: 'center', fontSize: 26, background: 'rgba(255,255,255,.16)' }}>
          <span className="howto-wave">📘</span>
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 17, fontWeight: 800 }}>How to use {g.title}</span>
          <span style={{ display: 'block', fontSize: 12.5, opacity: .88, marginTop: 3 }}>
            {tasks.length} task{tasks.length === 1 ? '' : 's'} · {totalSteps} steps · step-by-step walkthroughs
            {doneSteps > 0 && ` · ${doneTasks}/${tasks.length} tasks done`}
          </span>
          {doneSteps > 0 && (
            <span className="howto-bar" style={{ display: 'block', height: 5, maxWidth: 260, marginTop: 7, background: 'rgba(255,255,255,.25)', borderRadius: 99, overflow: 'hidden' }}>
              <i style={{ width: `${(doneSteps / totalSteps) * 100}%` }} />
            </span>
          )}
        </span>
        <span style={{ fontSize: 12, fontWeight: 800, padding: '7px 12px', borderRadius: 9, background: 'rgba(255,255,255,.18)', whiteSpace: 'nowrap' }}>{open ? 'Hide ▲' : 'Show ▼'}</span>
      </button>

      <div className={`howto-collapse${open ? ' open' : ''}`}>
        <div>
          <div style={{ padding: 20 }}>
            {g.intro && <p style={{ fontSize: 14, color: 'var(--textSecond)', margin: '0 0 14px', lineHeight: 1.7 }}>{g.intro}</p>}

            {/* How it works strip */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
              {[['🖱️', 'Click a task to open it'], ['👆', '“Show me” makes the real button glow'], ['▶', '“Guide me” walks you through'], ['✓', 'Tick each step as you go']].map(([ic, tx], n) => (
                <span key={tx} className="howto-fade" style={{ animationDelay: `${n * 70}ms`, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '6px 11px', borderRadius: 99,
                  background: 'var(--bgHover)', color: 'var(--textMuted)' }}><b style={{ color: 'var(--primary)' }}>{ic}</b>{tx}</span>
              ))}
            </div>

            {/* Search */}
            {tasks.length > 1 && (
              <div style={{ position: 'relative', marginBottom: 16 }}>
                <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', fontSize: 15, opacity: .6 }}>🔍</span>
                <input className="howto-search" value={query} onChange={e => setQuery(e.target.value)}
                  placeholder="What do you want to do? e.g. refund, check out, add item…"
                  style={{ width: '100%', boxSizing: 'border-box', padding: '12px 36px 12px 38px', borderRadius: 12, border: '1px solid var(--border)',
                    background: 'var(--bgInput)', color: 'var(--textPrimary)', fontSize: 14, fontFamily: 'inherit', outline: 'none', transition: 'border-color .2s, box-shadow .2s' }} />
                {query && (
                  <button onClick={() => setQuery('')} aria-label="Clear search"
                    style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent',
                      color: 'var(--textMuted)', cursor: 'pointer', fontSize: 15, padding: 6 }}>✕</button>
                )}
              </div>
            )}

            {/* Tasks */}
            <div style={{ display: 'grid', gap: 10 }}>
              {shown.length === 0 && (
                <div className="howto-fade" style={{ textAlign: 'center', padding: 22, color: 'var(--textMuted)', fontSize: 13.5 }}>
                  No task matches “{query}”. Try another word, or check “Having trouble?” below.
                </div>
              )}
              {shown.map(({ t, i }, n) => (
                <TaskCard key={t.h} task={t} idx={i} delay={Math.min(n * 45, 450)} query={q}
                  open={q ? true : openTask === i}
                  onToggle={() => setOpenTask(openTask === i ? -1 : i)}
                  done={doneMap[i] || new Set()}
                  onToggleStep={(si) => toggleStep(i, si)}
                  onResetTask={() => resetTask(i)}
                  onCoach={(ti) => setCoach(ti)} />
              ))}
            </div>

            {g.tip && <Callout kind="tip" title="Good to know"><Rich text={g.tip} /></Callout>}

            {/* FAQ */}
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: .8, textTransform: 'uppercase', color: 'var(--textMuted)', marginBottom: 8 }}>🛟 Having trouble?</div>
              <div style={{ display: 'grid', gap: 6 }}>
                {faq.map((f, i) => (
                  <div key={f.q} style={{ border: `1px solid ${faqOpen === i ? 'var(--primary)' : 'var(--border)'}`, borderRadius: 11, overflow: 'hidden', transition: 'border-color .2s' }}>
                    <button onClick={() => setFaqOpen(faqOpen === i ? null : i)} aria-expanded={faqOpen === i}
                      style={{ width: '100%', display: 'flex', justifyContent: 'space-between', gap: 10, padding: '11px 13px', background: 'transparent',
                        border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 650, color: 'var(--textPrimary)', textAlign: 'left' }}>
                      <span>❓ {f.q}</span>
                      <span style={{ transition: 'transform .3s', transform: faqOpen === i ? 'rotate(45deg)' : 'none', color: 'var(--primary)', fontSize: 18, lineHeight: 1 }}>+</span>
                    </button>
                    <div className={`howto-collapse${faqOpen === i ? ' open' : ''}`}>
                      <div><p style={{ margin: 0, padding: '0 13px 13px 35px', fontSize: 13.5, lineHeight: 1.65, color: 'var(--textSecond)' }}><Rich text={f.a} /></p></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <HelpFab targetRef={rootRef} hidden={coach !== null} onClick={jumpHere} title={g.title} />

      {coach !== null && tasks[coach] && createPortal(
        <Coach key={coach} task={tasks[coach]} onClose={() => setCoach(null)}
          onStepDone={(si) => toggleStep(coach, si, true)} />,
        document.body
      )}
    </div>
  );
}
